import React, { useMemo } from "react";
import { useDispatch } from "../redux/store";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState } from "../types";

interface RideOptionPickerProps {
  appointment: AppointmentState;
  complimentaryRide: string;
  setComplimentaryRide: (ride: string) => void;
  bounceComplimentaryRide: boolean;
  setBounceComplimentaryRide: (bounce: boolean) => void;
  setClear: (clear: boolean) => void;
}

const TRANSPORT_TYPES = [
  { flag: "shuttleEnabled", label: "Shuttle" },
  { flag: "loanerEnabled", label: "Loaner" },
  { flag: "rentalEnabled", label: "Rental Car" },
] as const;

export const RideOptionPicker: React.FC<RideOptionPickerProps> = ({
  appointment,
  complimentaryRide,
  setComplimentaryRide,
  bounceComplimentaryRide,
  setBounceComplimentaryRide,
  setClear,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const accentColor = appointment?.shopData?.color || "#2b73b9";

  const hasAnyTransportOption = useMemo(() => {
    const shop = appointment?.shopData || {};
    return TRANSPORT_TYPES.some((t) => shop[t.flag as keyof typeof shop]);
  }, [appointment?.shopData]);

  const transportationOptions = useMemo(() => {
    const shop = appointment?.shopData || {};
    return [
      "None",
      ...TRANSPORT_TYPES.filter((t) => shop[t.flag as keyof typeof shop]).map(
        (t) => t.label
      ),
    ];
  }, [appointment?.shopData]);

  if (!hasAnyTransportOption) {
    return null;
  }

  return (
    <div
      id="ride-picker"
      className="rounded-2xl bg-white p-5 font-roboto"
      style={{
        border: `2px solid ${accentColor}33`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <h3
        className="text-base font-bold text-center mb-4"
        style={{ color: "#1f2937" }}
      >
        Will you need a complimentary ride?
      </h3>

      <div className="grid grid-cols-2 gap-3">
        {transportationOptions.map((choice) => {
          const isSelected = choice === complimentaryRide;
          return (
            <button
              key={choice}
              type="button"
              className="flex items-center justify-center rounded-xl py-3 px-3 text-sm font-semibold transition-all cursor-pointer hover:scale-[1.02] font-roboto"
              style={{
                backgroundColor: isSelected ? accentColor : "#ffffff",
                border: `2px solid ${accentColor}`,
                color: isSelected ? "#ffffff" : accentColor,
              }}
              onClick={() => {
                dispatch(
                  updateAppointment({
                    updates: { complimentaryRide: choice },
                  })
                );
                setComplimentaryRide(choice);
                setBounceComplimentaryRide(true);
                if (!appointment?.shopData?.requireComments) {
                  setClear(true);
                }
              }}
            >
              {choice}
            </button>
          );
        })}
      </div>
    </div>
  );
};
